import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import BrandLoader from '../../../shared/brand/BrandLoader';
import Logo from '../../../shared/brand/Logo';
import { useTranslation } from 'react-i18next';
import {
  PiSparkleLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiClockLight,
  PiDownloadSimpleLight,
  PiSpinnerGapLight,
  PiArrowsClockwiseLight,
  PiMagnifyingGlassLight,
  PiArrowFatUpLight,
  PiMinusLight,
  PiArrowFatDownLight,
  PiListBulletsLight,
  PiLockSimpleLight,
} from 'react-icons/pi';
import clsx from 'clsx';
import { Tooltip } from '@mui/material';
import apis from '../../../../utils/apis';
import { useAppDispatch } from '../../../../store';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import Badge from '../../../shared/TailwindComponents/Badge';
import { BadgeColor } from '../../../shared/TailwindComponents/types/badgeTypes';
import Button from '../../../shared/TailwindComponents/Button';
import SubTabBar from '../../../shared/TailwindComponents/SubTabBar';
import FilterBarWithTabs from '../../../shared/TailwindComponents/FilterBarWithTabs';
import FindMoreProgress from '../../FindMoreProgress';
import { GenerateSuggestionsProgress } from '../../../../hooks/useGenerateSuggestions';
import { formatDate, formatDateTime, ensureUTCString, getIntlLocale } from '../../../../utils/functions';
import {
  MergedInsightItem,
  formatCategoryName,
  formatFileSize,
  getStatusColor,
  getStatusLabel,
} from './utils/insightUtils';
import MiniLoadingToCheckmark from '../../../shared/animations/MiniLoadingToCheckmark';
import { AiInsightSuggestion } from './InsightsTabContent';
import Skeleton from '../../../shared/TailwindComponents/Skeleton';

interface UnifiedInsightsViewProps {
  matterUuid: string | undefined;
  generatingInsightId: string | null;
  generatingInsightIds?: Set<string>;
  onGenerateInsight: (insightTypeId: string) => void;
  onViewInsight: (insightTypeId: string, insightName: string, versionId?: string) => void;
  onRefresh: () => void;
  onRefreshReady?: (refreshFn: () => Promise<void>) => void;
  processingMessage?: string;
  processingProgress?: number;
  aiSuggestions?: AiInsightSuggestion[] | null;
  isGeneratingSuggestions?: boolean;
  suggestionsProgress?: GenerateSuggestionsProgress;
  onGenerateSuggestions?: () => void;
  hasDocuments?: boolean;
  aiFeatureLocked?: boolean;
}

type ViewMode = 'table' | 'grid';

type InsightSubTab = 'suggested' | 'all';

const UnifiedInsightsView: React.FC<UnifiedInsightsViewProps> = ({
  matterUuid,
  generatingInsightId,
  generatingInsightIds,
  onGenerateInsight,
  onViewInsight,
  onRefresh,
  onRefreshReady,
  processingMessage,
  processingProgress,
  aiSuggestions,
  isGeneratingSuggestions,
  suggestionsProgress,
  onGenerateSuggestions,
  hasDocuments = false,
  aiFeatureLocked = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [searchTerm, setSearchTerm] = useState('');
  const [insightSubTab, setInsightSubTab] = useState<InsightSubTab>('suggested');
  const [mergedInsights, setMergedInsights] = useState<MergedInsightItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const onRefreshReadyRef = useRef(onRefreshReady);
  onRefreshReadyRef.current = onRefreshReady;
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const storageKey = 'insights-table-column-widths';
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse stored column widths:', e);
      }
    }
    return {
      'Name': 220,
      'Priority': 90,
      'Category': 120,
      'Status': 110,
      'Generated': 140,
      'Versions': 90,
      'Actions': 180,
    };
  });

  // Build AI suggestion map for O(1) lookups
  const aiSuggestionMap = useMemo(() => {
    const map = new Map<string, AiInsightSuggestion>();
    if (aiSuggestions) {
      aiSuggestions.forEach((s) => map.set(s.document_type_id, s));
    }
    return map;
  }, [aiSuggestions]);

  const [isResizing, setIsResizing] = useState(false);
  const resizingColRef = useRef<string | null>(null);
  const startXRef = useRef(0);

  const fetchMergedInsights = useCallback(async () => {
    if (!matterUuid) return;

    try {
      setLoading(true);
      const response = await apis.get(
        `/insights/list/${matterUuid}/merged`
      );

      if (response.status === 200) {
        setMergedInsights(response.data.documents || []);
      }
    } catch (error) {
      console.error('Failed to fetch merged insights:', error);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to load insights'),
          modalType: 'error',
          duration: 5000
        })
      );
    } finally {
      setLoading(false);
    }
  }, [matterUuid, dispatch]);

  useEffect(() => {
    fetchMergedInsights();
    if (onRefreshReadyRef.current) {
      onRefreshReadyRef.current(fetchMergedInsights);
    }
  }, [fetchMergedInsights]);

  const filteredInsights = useMemo(() => {
    let list = mergedInsights;

    // Apply sub-tab filter
    if (insightSubTab === 'suggested') {
      if (aiSuggestionMap.size > 0) {
        // Show only items that appear in the AI suggestion list
        list = list.filter((insight) => aiSuggestionMap.has(insight.document_type_id));
        // Sort by priority: high → medium → low
        const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
        list = [...list].sort((a, b) => {
          const pa = aiSuggestionMap.get(a.document_type_id)?.priority || 'low';
          const pb = aiSuggestionMap.get(b.document_type_id)?.priority || 'low';
          return (priorityOrder[pa] ?? 3) - (priorityOrder[pb] ?? 3);
        });
      } else {
        // No suggestions generated yet — show empty state
        list = [];
      }
    }

    // Apply search
    if (searchTerm) {
      list = list.filter((insight) =>
        insight.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        insight.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return list;
  }, [mergedInsights, searchTerm, insightSubTab, aiSuggestionMap]);

  const handleDownloadInsight = async (insightTypeId: string, insightName: string) => {
    if (!matterUuid) return;

    setDownloading(insightTypeId);
    try {
      const response = await apis.get(`/insights/${matterUuid}/${insightTypeId}`);

      if (response.status === 200) {
        const { content } = response.data;
        const element = document.createElement('a');
        const file = new Blob([content], { type: 'text/markdown' });
        element.href = URL.createObjectURL(file);
        element.download = `${insightName.replace(/\s+/g, '-')}.md`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Insight downloaded successfully'),
            modalType: 'success',
            duration: 3000
          })
        );
      }
    } catch (error) {
      console.error('Failed to download insight:', error);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to download insight'),
          modalType: 'error',
          duration: 5000
        })
      );
    } finally {
      setDownloading(null);
    }
  };

  const toggleVersions = (insightTypeId: string) => {
    const newExpanded = new Set(expandedVersions);
    if (newExpanded.has(insightTypeId)) {
      newExpanded.delete(insightTypeId);
    } else {
      newExpanded.add(insightTypeId);
    }
    setExpandedVersions(newExpanded);
  };

  const formatDateAndTime = (dateStr: string | null): { date: string; time: string } => {
    if (!dateStr) return { date: '', time: '' };
    const date = new Date(ensureUTCString(dateStr));
    const formattedDate = formatDate(dateStr);
    const time = date.toLocaleTimeString(getIntlLocale(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return { date: formattedDate, time };
  };

  const renderStatusBadge = (status: string) => {
    const color = getStatusColor(status);
    const label = getStatusLabel(status, t);
    let icon;

    if (status === 'completed') {
      icon = <PiCheckCircleLight className="h-3 w-3" />;
    } else if (status === 'processing') {
      icon = <MiniLoadingToCheckmark isLoading={true} spinnerSize={12} checkSize={12} />;
    } else if (status === 'failed') {
      icon = <PiWarningCircleLight className="h-3 w-3" />;
    }

    return (
      <Badge color={color} icon={icon}>
        {label}
      </Badge>
    );
  };

  const saveColumnWidths = (widths: Record<string, number>) => {
    const storageKey = 'insights-table-column-widths';
    localStorage.setItem(storageKey, JSON.stringify(widths));
  };

  const handleResizeStart = (e: React.MouseEvent, column: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizingColRef.current = column;
    startXRef.current = e.clientX;
  };

  useEffect(() => {
    if (!isResizing || !resizingColRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;
      const currentCol = resizingColRef.current;

      if (!currentCol) return;

      setColumnWidths((prev) => {
        const newWidths = { ...prev };
        const currentWidth = newWidths[currentCol] || 0;
        const minWidth = 80;
        const newWidth = Math.max(minWidth, currentWidth + delta);

        newWidths[currentCol] = newWidth;
        return newWidths;
      });

      startXRef.current = e.clientX;
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizingColRef.current = null;

      setColumnWidths((prev) => {
        saveColumnWidths(prev);
        return prev;
      });
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const renderActionButtons = (insight: MergedInsightItem) => {
    const isGenerating = generatingInsightIds?.has(insight.document_type_id) || generatingInsightId === insight.document_type_id;

    if (isGenerating || insight.status === 'processing') {
      return (
        <div className="flex items-center gap-2 max-w-[180px]">
          <MiniLoadingToCheckmark isLoading={true} spinnerSize={16} checkSize={16} />
          <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
            {isGenerating && processingMessage ? processingMessage : t('Processing')}
          </span>
        </div>
      );
    }

    if (insight.status === 'completed') {
      return (
        <Button
          variant="primary"
          size="small"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewInsight(insight.document_type_id, insight.name); }}
        >
          {t('View')}
        </Button>
      );
    }

    if (insight.status === 'failed') {
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="small"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onGenerateInsight(insight.document_type_id); }}
            disabled={!hasDocuments || aiFeatureLocked}
            tooltip={aiFeatureLocked ? t('Upgrade your plan to use AI Insights.') : !hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
            icon={aiFeatureLocked ? <PiLockSimpleLight className="h-3.5 w-3.5" /> : undefined}
          >
            {t('Retry')}
          </Button>
          <Button
            variant="outline"
            size="small"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewInsight(insight.document_type_id, insight.name); }}
          >
            {t('View')}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="small"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onGenerateInsight(insight.document_type_id); }}
          disabled={!hasDocuments || aiFeatureLocked}
          tooltip={aiFeatureLocked ? t('Upgrade your plan to use AI Insights.') : !hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
          icon={aiFeatureLocked ? <PiLockSimpleLight className="h-3.5 w-3.5" /> : undefined}
        >
          {t('Generate')}
        </Button>
        <Button
          variant="outline"
          size="small"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); onViewInsight(insight.document_type_id, insight.name); }}
        >
          {t('View')}
        </Button>
      </div>
    );
  };

  // Sub-tab options — always show both tabs (like Drafts)
  const subTabs = useMemo(() => {
    const suggestedCount = aiSuggestionMap.size > 0
      ? mergedInsights.filter((i) => aiSuggestionMap.has(i.document_type_id)).length
      : 0;
    return [
      { id: 'suggested' as InsightSubTab, label: t('Suggestions'), count: suggestedCount, icon: <PiSparkleLight className="h-3 w-3" />, color: 'yellow' as BadgeColor },
      { id: 'all' as InsightSubTab, label: t('All'), count: mergedInsights.length, icon: <PiListBulletsLight className="h-3 w-3" />, color: 'gray' as BadgeColor },
    ];
  }, [aiSuggestionMap, mergedInsights, t]);

  // Columns change based on whether suggestions are active
  const showPriorityColumn = insightSubTab === 'suggested' && aiSuggestionMap.size > 0;
  const tableColumns = showPriorityColumn
    ? ['Name', 'Priority', 'Category', 'Status', 'Generated', 'Versions', 'Actions']
    : ['Name', 'Category', 'Status', 'Generated', 'Versions', 'Actions'];

  if (loading && filteredInsights.length === 0) {
    return (
      <div className="bg-surface rounded-lg shadow-light dark:shadow-dark">
        <div className="p-4 border-b border-hairline">
          <FilterBarWithTabs
            onSearchChange={setSearchTerm}
            viewMode={viewMode}
            setViewMode={setViewMode}
            noParentPadding={true}
            noParentHeight={true}
            onRefresh={onRefresh}
            isRefreshLoading={loading}
            itemCount={0}
            totalItemCount={0}
            itemCountLabel={t('insights')}
          />
        </div>
        <div className="p-6">
          <Skeleton variant={viewMode === 'grid' ? 'grid' : 'table'} rows={viewMode === 'grid' ? 3 : 2} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded shadow-light dark:shadow-dark">
      {/* Sub-tab navigation — always visible */}
      <div data-tour-id="strategy-sub-tabs">
      <SubTabBar
        tabs={subTabs}
        activeTab={insightSubTab}
        onTabChange={(id) => setInsightSubTab(id as InsightSubTab)}
        className="px-4"
      />
      </div>
      <div data-tour-id="strategy-actions" className="p-4 border-b border-hairline">
        <FilterBarWithTabs
          onSearchChange={setSearchTerm}
          viewMode={viewMode}
          setViewMode={setViewMode}
          noParentPadding={true}
          noParentHeight={true}
          onRefresh={onRefresh}
          isRefreshLoading={loading}
          additionalControls={
            insightSubTab === 'suggested' && onGenerateSuggestions ? (
              <Button
                size="small"
                variant="primary"
                onClick={onGenerateSuggestions}
                disabled={isGeneratingSuggestions || !hasDocuments || aiFeatureLocked}
                tooltip={aiFeatureLocked ? t('Upgrade your plan to use AI Insights.') : !hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
                icon={aiFeatureLocked
                  ? <PiLockSimpleLight className="h-3.5 w-3.5" />
                  : isGeneratingSuggestions
                    ? <BrandLoader size="xs" className="!h-3.5 !w-3.5" />
                    : <PiSparkleLight className="h-3.5 w-3.5" />
                }
              >
                {isGeneratingSuggestions ? t('Generating...') : t('Generate Suggestions')}
              </Button>
            ) : undefined
          }
        />
      </div>

      {/* Suggestion generation progress overlay */}
      {insightSubTab === 'suggested' && suggestionsProgress && (isGeneratingSuggestions || suggestionsProgress.status === 'complete' || suggestionsProgress.status === 'failed') && (
        <FindMoreProgress
          progress={{ message: suggestionsProgress.message, percent: suggestionsProgress.percent, status: suggestionsProgress.status }}
          isGenerating={!!isGeneratingSuggestions}
        />
      )}

      {filteredInsights.length === 0 && !loading && !(insightSubTab === 'suggested' && (isGeneratingSuggestions || suggestionsProgress?.status === 'complete' || suggestionsProgress?.status === 'failed')) && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <PiSparkleLight className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            {t('No insights found')}
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 max-w-xs">
            {searchTerm
              ? t('Try adjusting your search terms')
              : insightSubTab === 'suggested' && (!aiSuggestions || aiSuggestions.length === 0)
                ? t('Generate AI-powered suggestions to see which insights are most relevant for this matter')
                : t('Select an insight type to generate your first insight')}
          </p>
          {insightSubTab === 'suggested' && !searchTerm && (!aiSuggestions || aiSuggestions.length === 0) && onGenerateSuggestions && (
            <Button
              size='small'
              variant='primary'
              onClick={onGenerateSuggestions}
              disabled={isGeneratingSuggestions || !hasDocuments}
              tooltip={!hasDocuments ? t('Upload at least one case file to enable AI generation.') : ''}
              icon={isGeneratingSuggestions
                ? <BrandLoader size="xs" className="!h-3.5 !w-3.5" />
                : <PiSparkleLight className="h-3.5 w-3.5" />
              }
              className='mt-4' 
            >
              {isGeneratingSuggestions ? t('Generating...') : t('Generate Suggestions')}
            </Button>
          )}
        </div>
      )}

      {viewMode === 'table' && filteredInsights.length > 0 && !(insightSubTab === 'suggested' && (isGeneratingSuggestions || suggestionsProgress?.status === 'complete' || suggestionsProgress?.status === 'failed')) && (
        <div className="overflow-x-auto custom-scrollbar-main" style={{ userSelect: isResizing ? 'none' : 'auto' }}>
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="border-b border-hairline bg-surface-sunken/70">
                {tableColumns.map((col, idx) => {
                  const colWidth = columnWidths[col];
                  const isLastColumn = idx === tableColumns.length - 1;

                  return (
                    <th
                      key={col}
                      className={clsx(
                        'relative px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300',
                        col === 'Actions' && 'text-right'
                      )}
                      style={colWidth ? { width: `${colWidth}px`, minWidth: `${colWidth}px` } : undefined}
                    >
                      <div className="flex items-center justify-between h-full">
                        <span>{t(col)}</span>
                        {!isLastColumn && col !== 'Actions' && (
                          <div
                            onMouseDown={(e) => handleResizeStart(e, col)}
                            className={clsx(
                              'table-resize-handle',
                              isResizing && resizingColRef.current === col && 'resizing'
                            )}
                            title={t('Drag to resize column')}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredInsights.map((insight) => {
                const { date, time } = (insight as any).generated_at
                  ? formatDateAndTime((insight as any).generated_at)
                  : { date: '', time: '' };
                const isExpanded = expandedVersions.has(insight.document_type_id);
                const isGenerated = insight.status === 'completed' && (insight as any).generated_at;
                const isProcessing = generatingInsightIds?.has(insight.document_type_id) || generatingInsightId === insight.document_type_id || insight.status === 'processing';
                const suggestion = aiSuggestionMap.get(insight.document_type_id);

                return (
                  <React.Fragment key={insight.document_type_id}>
                    <tr
                      className={clsx(
                        'transition-colors cursor-pointer',
                        isProcessing
                          ? 'bg-info-50/50 dark:bg-info-900/10 hover:bg-info-50 dark:hover:bg-info-900/20'
                          : isGenerated
                          ? 'hover:bg-info-50 dark:hover:bg-info-900/20'
                          : 'hover:bg-surface-sunken/70'
                      )}
                      onClick={() => onViewInsight(insight.document_type_id, insight.name)}
                    >
                      <td className="px-4 py-4" style={{ width: `${columnWidths['Name']}px`, minWidth: `${columnWidths['Name']}px` }}>
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {insight.name}
                            </p>
                            {/* Show AI reason as subtitle when available; otherwise show description */}
                            {suggestion ? (
                              <Tooltip
                                title={`${t('Recommended by PONS')}: ${suggestion.reason || ''}`}
                                arrow
                                placement="bottom-start"
                                enterDelay={300}
                                slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: '0.75rem', lineHeight: 1.5 } } }}
                              >
                                <div className="flex items-start gap-1 mt-0.5 cursor-help">
                                  <PiSparkleLight className="h-3.5 w-3.5 mt-px flex-shrink-0 text-info-500 dark:text-info-400" />
                                  <p className="text-xs text-info-600 dark:text-info-400 line-clamp-2">
                                    {suggestion.reason}
                                  </p>
                                </div>
                              </Tooltip>
                            ) : (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 hover:line-clamp-none cursor-help" title={insight.description}>
                                {insight.description}
                              </p>
                            )}
                          </div>
                          {insight.isPonsCreated && (
                            <span title={t('Created by PONS')}>
                              <Logo variant="mark" whiteLabelAware={false} className="h-4 w-4 opacity-70 flex-shrink-0" />
                            </span>
                          )}
                        </div>
                      </td>

                      {showPriorityColumn && (
                        <td className="px-4 py-4" style={{ width: `${columnWidths['Priority']}px`, minWidth: `${columnWidths['Priority']}px` }}>
                          {suggestion && (() => {
                            const priorityConfig = {
                              high: { icon: <PiArrowFatUpLight className="h-3 w-3" />, color: 'red' as const, label: t('High') },
                              medium: { icon: <PiMinusLight className="h-3 w-3" />, color: 'yellow' as const, label: t('Medium') },
                              low: { icon: <PiArrowFatDownLight className="h-3 w-3" />, color: 'blue' as const, label: t('Low') },
                            };
                            const c = priorityConfig[suggestion.priority || 'low'];
                            return (
                              <Badge color={c.color} icon={c.icon}>
                                {c.label}
                              </Badge>
                            );
                          })()}
                        </td>
                      )}

                      <td className="px-4 py-4" style={{ width: `${columnWidths['Category']}px`, minWidth: `${columnWidths['Category']}px` }}>
                        <Badge color="gray">{formatCategoryName(insight.category)}</Badge>
                      </td>

                      <td className="px-4 py-4" style={{ width: `${columnWidths['Status']}px`, minWidth: `${columnWidths['Status']}px` }}>
                        {insight.finalized ? (
                          <Badge
                            color="green"
                            icon={<PiCheckCircleLight className="h-3 w-3" />}
                            tooltip={insight.finalized_at ? `Finalized on ${formatDateTime(insight.finalized_at)}` : undefined}
                          >
                            {t('Finalized')}
                          </Badge>
                        ) : (
                          <Badge color="gray">
                            {t('Not Finalized')}
                          </Badge>
                        )}
                      </td>

                      <td className="px-4 py-4" style={{ width: `${columnWidths['Generated']}px`, minWidth: `${columnWidths['Generated']}px` }}>
                        {(insight as any).generated_at ? (
                          <div>
                            <span className="text-sm text-gray-900 dark:text-white">{date}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{time}</span>
                          </div>
                        ) : insight.status === 'processing' ? (
                          <Badge color="blue" icon={<MiniLoadingToCheckmark isLoading={true} spinnerSize={12} checkSize={12} />}>
                            {t('Processing')}
                          </Badge>
                        ) : (
                          <Badge color="gray">{t('Not Generated')}</Badge>
                        )}
                      </td>

                      <td className="px-4 py-4" style={{ width: `${columnWidths['Versions']}px`, minWidth: `${columnWidths['Versions']}px` }} onClick={(e) => e.stopPropagation()}>
                        {insight.allVersions.length > 0 ? (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleVersions(insight.document_type_id);
                            }}
                            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline focus:outline-none"
                          >
                            {insight.allVersions.length}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">—</span>
                        )}
                      </td>

                      <td className="px-4 py-4 text-right" style={{ width: `${columnWidths['Actions']}px`, minWidth: `${columnWidths['Actions']}px` }} onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end">{renderActionButtons(insight)}</div>
                      </td>
                    </tr>

                    {isExpanded && insight.allVersions.length > 0 && (
                      <tr className="bg-surface-sunken/50">
                        <td colSpan={tableColumns.length} className="px-4 py-4">
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">
                              Version History
                            </p>
                            {insight.allVersions.map((version, idx) => {
                              const versionDate = formatDateAndTime(version.modified);
                              const versionId = (version as any).version_id;
                              const isFinalizedVersion = insight.finalized && idx === 0;
                              const canSelectVersion = !insight.finalized || isFinalizedVersion;
                              return (
                                <button
                                  key={`${insight.document_type_id}-${versionId}`}
                                  onClick={() => canSelectVersion && onViewInsight(insight.document_type_id, insight.name, versionId)}
                                  disabled={!canSelectVersion}
                                  title={!canSelectVersion ? t('Cannot view older versions of a finalized insight') : undefined}
                                  className={`w-full flex items-center justify-between p-3 bg-surface rounded border border-hairline transition-all ${
                                    canSelectVersion
                                      ? 'hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-sm cursor-pointer'
                                      : 'opacity-50 cursor-not-allowed'
                                  }`}
                                >
                                  <div className="flex-1 text-left">
                                    <div className="flex items-center gap-3">
                                      <Badge color="gray">v{version.version || idx + 1}</Badge>
                                      <span className="text-xs text-gray-600 dark:text-gray-400">
                                        {versionDate.date} at {versionDate.time}
                                      </span>
                                      {isFinalizedVersion && (
                                        <Badge color="green">
                                          <span className="text-xs flex items-center gap-1">
                                            <PiCheckCircleLight className="h-2.5 w-2.5" />
                                            Final
                                          </span>
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === 'grid' && filteredInsights.length > 0 && !(insightSubTab === 'suggested' && (isGeneratingSuggestions || suggestionsProgress?.status === 'complete' || suggestionsProgress?.status === 'failed')) && (
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInsights.map((insight) => {
              const isGenerating = generatingInsightIds?.has(insight.document_type_id) || generatingInsightId === insight.document_type_id;
              const { date, time } = (insight as any).generated_at
                ? formatDateAndTime((insight as any).generated_at)
                : { date: '', time: '' };

              return (
                <div
                  key={insight.document_type_id}
                  className="p-4 border border-hairline rounded-lg hover:shadow-md dark:hover:shadow-dark transition-shadow"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {insight.name}
                      </h4>
                      {(() => {
                        const gridSuggestion = aiSuggestions?.find(s => s.document_type_id === insight.document_type_id);
                        return gridSuggestion ? (
                          <Tooltip
                            title={`${t('Recommended by PONS')}: ${gridSuggestion.reason || ''}`}
                            arrow
                            placement="bottom-start"
                            enterDelay={300}
                            slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: '0.75rem', lineHeight: 1.5 } } }}
                          >
                            <div className="flex items-start gap-1 mt-1 cursor-help">
                              <PiSparkleLight className="h-3.5 w-3.5 mt-px flex-shrink-0 text-info-500 dark:text-info-400" />
                              <p className="text-xs text-info-600 dark:text-info-400 line-clamp-2">
                                {gridSuggestion.reason}
                              </p>
                            </div>
                          </Tooltip>
                        ) : (
                          <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-1">
                            {insight.description}
                          </p>
                        );
                      })()}
                    </div>
                    {insight.isPonsCreated && (
                      <Logo variant="mark" whiteLabelAware={false} className="h-4 w-4 opacity-70 flex-shrink-0" />
                    )}
                  </div>

                  <div className="space-y-2 mb-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Badge color="gray">{formatCategoryName(insight.category)}</Badge>
                      {insight.finalized ? (
                        <Badge
                          color="green"
                          icon={<PiCheckCircleLight className="h-3 w-3" />}
                          tooltip={insight.finalized_at ? `Finalized on ${formatDateTime(insight.finalized_at)}` : undefined}
                        >
                          {t('Finalized')}
                        </Badge>
                      ) : (
                        <Badge color="gray">{t('Not Finalized')}</Badge>
                      )}
                    </div>

                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {(insight as any).generated_at ? (
                        <>
                          <p>Generated: {date} {time}</p>
                          <p>Versions: {insight.allVersions.length}</p>
                        </>
                      ) : insight.status === 'processing' ? (
                        <p>{t('Processing...')}</p>
                      ) : (
                        <p>{t('Not Generated')}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">{renderActionButtons(insight)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedInsightsView;
